import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { MessageVersion } from '../../hooks/useChatThread'

interface Props {
  messageId: string
  load: (messageId: string) => Promise<MessageVersion[]>
  onClose: () => void
}

function fmt(v: string): string {
  try {
    return new Date(v).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return v
  }
}

/**
 * Vertical timeline of every version of an edited message: the original at the
 * top, each subsequent edit below, current last. Internal record — the customer
 * never sees this (and never sees that the message was changed at all).
 */
export const MessageVersionsModal: React.FC<Props> = ({ messageId, load, onClose }) => {
  const [versions, setVersions] = useState<MessageVersion[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    load(messageId)
      .then((v) => { if (active) setVersions(v) })
      .catch(() => { if (active) setError('Could not load versions') })
    return () => { active = false }
  }, [messageId, load])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-surface rounded-xl border border-border shadow-float w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <h3 className="text-sm font-semibold text-txt-primary">Message versions</h3>
            <p className="text-[11px] text-txt-tertiary">Internal record — not visible to the customer</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {error ? (
            <p className="text-sm text-red-500">{error}</p>
          ) : !versions ? (
            <p className="text-sm text-txt-tertiary">Loading…</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-txt-tertiary">No previous versions.</p>
          ) : (
            <ol className="relative border-l-2 border-border ml-2">
              {versions.map((v, i) => {
                const isCurrent = i === versions.length - 1
                const isOriginal = i === 0
                return (
                  <li key={v.id} className="mb-5 last:mb-0 ml-5">
                    {/* Timeline dot */}
                    <span
                      className={`absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full ring-4 ring-surface ${
                        isCurrent ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          isCurrent
                            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                            : 'bg-surface-secondary text-txt-tertiary'
                        }`}
                      >
                        {isOriginal ? 'Original' : isCurrent ? 'Current' : `Edit ${i}`}
                      </span>
                      <span className="text-[11px] text-txt-tertiary">
                        {fmt(v.created_at)}
                        {v.edited_by_name ? ` · ${v.edited_by_name}` : ''}
                      </span>
                    </div>
                    <div
                      className={`mt-1.5 rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words border ${
                        isCurrent
                          ? 'bg-surface border-brand-200 text-txt-primary'
                          : 'bg-surface-secondary border-border text-txt-secondary line-through decoration-txt-tertiary/40'
                      }`}
                    >
                      {v.body || <span className="italic opacity-60">(empty)</span>}
                    </div>
                  </li>
                )
              })}
            </ol>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
